// src/pages/admin/AdminDonations.jsx
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Search,
  Filter,
  Eye,
  DollarSign,
  User,
  Calendar,
  RefreshCw,
  Download,
  TrendingUp, 
  TrendingDown,
  CheckCircle,
  XCircle,
  Clock,
  ChevronDown, 
  ChevronUp,
  FileText,
  Mail
} from 'lucide-react';
import styles from '../../styles/Donation.module.css';
import { toast } from 'react-hot-toast';
import useTranslation from '../../hooks/useTranslation';

// Import from donationSlice
import {
  getAllDonations,
  getDonationById,
  sendThankYou,
  confirmDonation,
  refundDonation,
  addAdminNote,
  clearDonationErrors,
  clearDonationSuccess,
  setDonationFilters,
  setDonationPagination,
  resetDonationState
} from '../../store/slices/donationSlice';

import {
  selectDonations,
  selectDonationPagination,
  selectDonationsLoading,
  selectDonationLoading,
  selectDonationSubmitting,
  selectDonationUpdating,
  selectDonationError,
  selectSubmitDonationError,
  selectUpdateDonationError,
  selectCurrentDonation,
  selectTotalDonationsCount,
  selectCompletedDonations,
  selectPendingDonations,
  selectTotalDonationAmount,
  selectDonationStatistics
} from '../../store/slices/donationSlice';

const AdminDonations = () => {
  const dispatch = useDispatch();
  
  // Redux selectors
  const donations = useSelector(selectDonations);
  const pagination = useSelector(selectDonationPagination);
  const loading = useSelector(selectDonationsLoading);
  const loadingDonation = useSelector(selectDonationLoading);
  const isSubmitting = useSelector(selectDonationSubmitting);
  const isUpdating = useSelector(selectDonationUpdating);
  const error = useSelector(selectDonationError);
  const currentDonation = useSelector(selectCurrentDonation);
  const totalDonationsCount = useSelector(selectTotalDonationsCount);
  const completedDonations = useSelector(selectCompletedDonations);
  const pendingDonations = useSelector(selectPendingDonations);
  const totalDonationAmount = useSelector(selectTotalDonationAmount);
  
  // Local state
  const [selectedDonation, setSelectedDonation] = useState(null);
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetails, setShowDetails] = useState(false);
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const [adminNote, setAdminNote] = useState('');

  // Translation hooks
  const { translatedText: donationsLabel } = useTranslation('Donations');
  const { translatedText: total } = useTranslation('Total');
  const { translatedText: totalAmount } = useTranslation('Total Amount');
  const { translatedText: completed } = useTranslation('Completed');
  const { translatedText: pending } = useTranslation('Pending');
  const { translatedText: failed } = useTranslation('Failed');
  const { translatedText: refunded } = useTranslation('Refunded');
  const { translatedText: exportLabel } = useTranslation('Export');
  const { translatedText: refresh } = useTranslation('Refresh');
  const { translatedText: allDonations } = useTranslation('All Donations');
  const { translatedText: searchPlaceholder } = useTranslation('Search donations...');
  const { translatedText: from } = useTranslation('From');
  const { translatedText: to } = useTranslation('To');
  const { translatedText: apply } = useTranslation('Apply');
  const { translatedText: donationNumber } = useTranslation('Donation #');
  const { translatedText: donor } = useTranslation('Donor');
  const { translatedText: amount } = useTranslation('Amount');
  const { translatedText: purpose } = useTranslation('Purpose');
  const { translatedText: paymentMethod } = useTranslation('Payment Method');
  const { translatedText: status } = useTranslation('Status');
  const { translatedText: date } = useTranslation('Date');
  const { translatedText: actions } = useTranslation('Actions');
  const { translatedText: noDonations } = useTranslation('No donations found');
  const { translatedText: noDonationsDesc } = useTranslation('There are no donations matching your criteria');
  const { translatedText: previous } = useTranslation('Previous');
  const { translatedText: next } = useTranslation('Next');
  const { translatedText: page } = useTranslation('Page');
  const { translatedText: donationDetails } = useTranslation('Donation Details');
  const { translatedText: donationInformation } = useTranslation('Donation Information');
  const { translatedText: donorInformation } = useTranslation('Donor Information');
  const { translatedText: message } = useTranslation('Message');
  const { translatedText: providerSupport } = useTranslation('Provider Support');
  const { translatedText: providerName } = useTranslation('Provider Name');
  const { translatedText: providerId } = useTranslation('Provider ID');
  const { translatedText: adminNotes } = useTranslation('Admin Notes');
  const { translatedText: sendReceipt } = useTranslation('Send Receipt');
  const { translatedText: exportDetails } = useTranslation('Export Details');
  const { translatedText: loadingDonations } = useTranslation('Loading donations...');
  const { translatedText: exportStarted } = useTranslation('Export started');
  const { translatedText: receiptSent } = useTranslation('Receipt sent to donor');
  const { translatedText: exportingDetails } = useTranslation('Exporting donation details');
  const { translatedText: anonymous } = useTranslation('Anonymous');
  const { translatedText: nA } = useTranslation('N/A');
  const { translatedText: yes } = useTranslation('Yes');
  const { translatedText: no } = useTranslation('No');
  const { translatedText: general } = useTranslation('General');
  const { translatedText: pageOf } = useTranslation('Page {page} of {totalPages}');
  const { translatedText: addNote } = useTranslation('Add Note');
  const { translatedText: notePlaceholder } = useTranslation('Add admin note...');

  // Calculate stats from Redux data
  const stats = {
    total: totalDonationsCount || donations?.length || 0,
    totalAmount: totalDonationAmount || 0,
    completed: completedDonations?.length || 0,
    pending: pendingDonations?.length || 0,
    failed: donations?.filter(d => d.status === 'failed').length || 0,
    refunded: donations?.filter(d => d.status === 'refunded').length || 0
  };

  useEffect(() => {
    fetchDonations();
    
    // Cleanup on unmount
    return () => {
      dispatch(resetDonationState());
    };
  }, []);

  useEffect(() => {
    // Re-fetch when filter, search, or pagination changes
    fetchDonations();
  }, [filter, pagination.page, pagination.limit]);

  const fetchDonations = () => {
    const params = {
      page: pagination.page || 1,
      limit: pagination.limit || 20,
      sortBy,
      sortOrder
    };

    // Add status filter
    if (filter !== 'all') {
      params.status = filter;
    }

    // Add search filter
    if (searchTerm) {
      params.search = searchTerm;
    }

    // Add date range
    if (dateRange.start) {
      params.startDate = dateRange.start;
    }
    if (dateRange.end) {
      params.endDate = dateRange.end;
    }

    dispatch(getAllDonations(params));
  };

  const handleSearch = () => {
    fetchDonations();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const viewDetails = async (donation) => {
    try {
      await dispatch(getDonationById(donation._id)).unwrap();
      setSelectedDonation(donation);
      setShowDetails(true);
    } catch (error) {
      toast.error('Failed to load donation details');
    }
  };

  const handleSendThankYou = async () => {
    if (!selectedDonation) return;
    
    try {
      await dispatch(sendThankYou(selectedDonation._id)).unwrap();
      toast.success(receiptSent || 'Thank you email sent to donor');
      
      // Update local donation
      setSelectedDonation({
        ...selectedDonation,
        thankYouSent: true
      });
    } catch (error) {
      toast.error('Failed to send thank you email');
    }
  };

  const handleAddAdminNote = async () => {
    if (!selectedDonation || !adminNote.trim()) return;
    
    try {
      await dispatch(addAdminNote({
        id: selectedDonation._id,
        note: adminNote
      })).unwrap();
      
      toast.success('Admin note added successfully');
      setAdminNote('');
      
      // Refresh donation details
      await dispatch(getDonationById(selectedDonation._id)).unwrap();
      
    } catch (error) {
      toast.error('Failed to add admin note');
    }
  };

  const handleConfirmDonation = async (donationId) => {
    try {
      await dispatch(confirmDonation(donationId)).unwrap();
      toast.success('Donation confirmed successfully');
      fetchDonations();
    } catch (error) {
      toast.error('Failed to confirm donation');
    }
  };

  const handleRefundDonation = async (donationId) => {
    if (!window.confirm('Are you sure you want to refund this donation?')) return;
    
    try {
      await dispatch(refundDonation({ id: donationId })).unwrap();
      toast.success('Donation refunded successfully');
      fetchDonations();
    } catch (error) {
      toast.error('Failed to refund donation');
    }
  };

  const getStatusBadge = (status) => {
    const statusLabels = {
      pending: pending || 'Pending',
      completed: completed || 'Completed',
      failed: failed || 'Failed',
      refunded: refunded || 'Refunded'
    };
    
    switch(status) {
      case 'pending':
        return <span className={styles.badgePending}><Clock size={14} /> {statusLabels.pending}</span>;
      case 'completed':
        return <span className={styles.badgeCompleted}><CheckCircle size={14} /> {statusLabels.completed}</span>;
      case 'failed':
        return <span className={styles.badgeFailed}><XCircle size={14} /> {statusLabels.failed}</span>;
      case 'refunded':
        return <span className={styles.badgeRefunded}>{statusLabels.refunded}</span>;
      default:
        return null;
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getPurposeLabel = (purpose) => {
    const purposeMap = {
      'community_development': 'Community Development',
      'platform_support': 'Platform Support',
      'technical_support': 'Technical Support',
      'general': general || 'General'
    };
    return purposeMap[purpose] || purpose?.replace(/_/g, ' ') || general || 'General';
  };

  const filterOptions = [
    { value: 'all', label: allDonations || 'All Donations' },
    { value: 'pending', label: pending || 'Pending' },
    { value: 'completed', label: completed || 'Completed' },
    { value: 'failed', label: failed || 'Failed' },
    { value: 'refunded', label: refunded || 'Refunded' }
  ];

  const handlePageChange = (newPage) => {
    if (newPage < 1 || newPage > pagination.pages) return;
    dispatch(setDonationPagination({ page: newPage }));
  };

  if (loading && donations.length === 0) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>{loadingDonations || 'Loading donations...'}</p>
      </div>
    );
  }

  return (
    <div className={styles.donationsContainer}>
      {/* Header */}
      <div className={styles.sectionHeader}>
        <div className={styles.headerLeft}>
          <h2>{donationsLabel || 'Donations'}</h2>
          <div className={styles.statsBadges}>
            <span className={styles.statBadge}>
              {total || 'Total'}: {stats.total}
            </span>
            <span className={styles.statBadgeAmount}>
              {totalAmount || 'Total Amount'}: {formatCurrency(stats.totalAmount)}
            </span>
            <span className={styles.statBadgeCompleted}>
              {completed || 'Completed'}: {stats.completed}
            </span>
            <span className={styles.statBadgePending}>
              {pending || 'Pending'}: {stats.pending}
            </span>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button 
            className={styles.exportBtn}
            onClick={() => toast.success(exportStarted || 'Export started')}
          >
            <Download size={18} />
            {exportLabel || 'Export'}
          </button>
          <button className={styles.refreshBtn} onClick={fetchDonations}>
            <RefreshCw size={18} />
            {refresh || 'Refresh'}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className={styles.filterSection}>
        <div className={styles.filterTabs}>
          {filterOptions.map((option) => (
            <button
              key={option.value}
              className={`${styles.filterTab} ${filter === option.value ? styles.active : ''}`}
              onClick={() => setFilter(option.value)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className={styles.searchBox}>
          <Search size={18} />
          <input
            type="text"
            placeholder={searchPlaceholder || 'Search donations...'}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <button className={styles.searchBtn} onClick={handleSearch}>
            Search
          </button>
        </div>
      </div>

      {/* Date Range Filter */}
      <div className={styles.dateFilter}>
        <label>
          {from || 'From'}:
          <input 
            type="date" 
            value={dateRange.start}
            onChange={(e) => setDateRange({...dateRange, start: e.target.value})}
          />
        </label>
        <label>
          {to || 'To'}:
          <input 
            type="date" 
            value={dateRange.end}
            onChange={(e) => setDateRange({...dateRange, end: e.target.value})}
          />
        </label>
        <button className={styles.applyDateBtn} onClick={fetchDonations}>
          {apply || 'Apply'}
        </button>
      </div>

      {/* Donations Table */}
      <div className={styles.tableContainer}>
        <table className={styles.donationsTable}>
          <thead>
            <tr>
              <th>{donationNumber || 'Donation #'}</th>
              <th>{donor || 'Donor'}</th>
              <th>{amount || 'Amount'}</th>
              <th>{purpose || 'Purpose'}</th>
              <th>{paymentMethod || 'Payment Method'}</th>
              <th>{status || 'Status'}</th>
              <th>{date || 'Date'}</th>
              <th>{actions || 'Actions'}</th>
            </tr>
          </thead>
          <tbody>
            {donations.length === 0 ? (
              <tr>
                <td colSpan="8" className={styles.emptyState}>
                  <FileText size={48} />
                  <h3>{noDonations || 'No donations found'}</h3>
                  <p>{noDonationsDesc || 'There are no donations matching your criteria'}</p>
                </td>
              </tr>
            ) : (
              donations.map((donation) => (
                <tr key={donation._id}>
                  <td className={styles.donationNumber}>{donation.donationNumber}</td>
                  <td>
                    <div className={styles.donorInfo}>
                      {donation.isAnonymous ? (
                        <span className={styles.anonymous}>{anonymous || 'Anonymous'}</span>
                      ) : (
                        <>
                          <span className={styles.donorName}>{donation.donorName || nA || 'N/A'}</span>
                          <span className={styles.donorEmail}>{donation.donorEmail}</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className={styles.amount}>
                    {formatCurrency(donation.amount, donation.currency)}
                  </td>
                  <td>
                    <span className={styles.purposeBadge}>
                      {getPurposeLabel(donation.purpose)}
                    </span>
                  </td>
                  <td>
                    <span className={styles.paymentMethod}>
                      {donation.paymentMethod?.toUpperCase() || nA || 'N/A'}
                    </span>
                  </td>
                  <td>{getStatusBadge(donation.status)}</td>
                  <td>
                    <span className={styles.dateCell}>
                      {new Date(donation.createdAt).toLocaleDateString()}
                    </span>
                  </td>
                  <td>
                    <button 
                      className={styles.viewBtn}
                      onClick={() => viewDetails(donation)}
                    >
                      <Eye size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className={styles.pagination}>
          <button 
            className={styles.pageBtn}
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page <= 1}
          >
            {previous || 'Previous'}
          </button>
          <span className={styles.pageInfo}>
            {page || 'Page'} {pagination.page} {pageOf ? pageOf.replace('{page}', pagination.page).replace('{totalPages}', pagination.pages) : `of ${pagination.pages}`}
          </span>
          <button 
            className={styles.pageBtn}
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page >= pagination.pages}
          >
            {next || 'Next'}
          </button>
        </div>
      )}

      {/* Details Modal */}
      {showDetails && selectedDonation && (
        <div className={styles.modalOverlay} onClick={() => setShowDetails(false)}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3>{donationDetails || 'Donation Details'}</h3>
              <button className={styles.closeBtn} onClick={() => setShowDetails(false)}>
                <XCircle size={24} />
              </button>
            </div>
            <div className={styles.modalBody}>
              <div className={styles.detailSection}>
                <h4>{donationInformation || 'Donation Information'}</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <label>{donationNumber || 'Donation #'}</label>
                    <p>{selectedDonation.donationNumber}</p>
                  </div>
                  <div className={styles.detailItem}>
                    <label>{amount || 'Amount'}</label>
                    <p className={styles.amountHighlight}>
                      {formatCurrency(selectedDonation.amount, selectedDonation.currency)}
                    </p>
                  </div>
                  <div className={styles.detailItem}>
                    <label>{status || 'Status'}</label>
                    <p>{getStatusBadge(selectedDonation.status)}</p>
                  </div>
                  <div className={styles.detailItem}>
                    <label>{purpose || 'Purpose'}</label>
                    <p>{getPurposeLabel(selectedDonation.purpose)}</p>
                  </div>
                  <div className={styles.detailItem}>
                    <label>{paymentMethod || 'Payment Method'}</label>
                    <p>{selectedDonation.paymentMethod?.toUpperCase() || nA || 'N/A'}</p>
                  </div>
                  <div className={styles.detailItem}>
                    <label>{date || 'Date'}</label>
                    <p>{new Date(selectedDonation.createdAt).toLocaleString()}</p>
                  </div>
                  {selectedDonation.completedAt && (
                    <div className={styles.detailItem}>
                      <label>Completed Date</label>
                      <p>{new Date(selectedDonation.completedAt).toLocaleString()}</p>
                    </div>
                  )}
                  {selectedDonation.stripePaymentId && (
                    <div className={styles.detailItem}>
                      <label>Stripe Payment ID</label>
                      <p className={styles.monoText}>{selectedDonation.stripePaymentId}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className={styles.detailSection}>
                <h4>{donorInformation || 'Donor Information'}</h4>
                <div className={styles.detailGrid}>
                  <div className={styles.detailItem}>
                    <label>{donor || 'Name'}</label>
                    <p>{selectedDonation.isAnonymous ? (anonymous || 'Anonymous') : (selectedDonation.donorName || nA || 'N/A')}</p>
                  </div>
                  <div className={styles.detailItem}>
                    <label>Email</label>
                    <p>{selectedDonation.donorEmail || nA || 'N/A'}</p>
                  </div>
                  <div className={styles.detailItem}>
                    <label>{anonymous || 'Anonymous'}</label>
                    <p>{selectedDonation.isAnonymous ? (yes || 'Yes') : (no || 'No')}</p>
                  </div>
                </div>
              </div>

              {selectedDonation.message && (
                <div className={styles.detailSection}>
                  <h4>{message || 'Message'}</h4>
                  <p className={styles.message}>{selectedDonation.message}</p>
                </div>
              )}

              {selectedDonation.adminNotes && selectedDonation.adminNotes.length > 0 && (
                <div className={styles.detailSection}>
                  <h4>{adminNotes || 'Admin Notes'}</h4>
                  <div className={styles.notesList}>
                    {selectedDonation.adminNotes.map((note, index) => (
                      <div key={index} className={styles.noteItem}>
                        <p>{typeof note === 'string' ? note : note.note || note.message}</p>
                        <span className={styles.noteDate}>
                          {note.date ? new Date(note.date).toLocaleString() : ''}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Add Admin Note */}
              <div className={styles.detailSection}>
                <h4>{addNote || 'Add Admin Note'}</h4>
                <div className={styles.addNoteContainer}>
                  <textarea
                    className={styles.noteInput}
                    placeholder={notePlaceholder || 'Add admin note...'}
                    value={adminNote}
                    onChange={(e) => setAdminNote(e.target.value)}
                    rows={2}
                  />
                  <button 
                    className={styles.addNoteBtn}
                    onClick={handleAddAdminNote}
                    disabled={!adminNote.trim() || isUpdating}
                  >
                    {isUpdating ? 'Adding...' : (addNote || 'Add Note')}
                  </button>
                </div>
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button 
                className={styles.sendReceiptBtn}
                onClick={handleSendThankYou}
                disabled={selectedDonation.thankYouSent}
              >
                <Mail size={18} />
                {selectedDonation.thankYouSent ? 'Thank You Sent' : (sendReceipt || 'Send Receipt')}
              </button>
              {selectedDonation.status === 'pending' && (
                <button 
                  className={styles.confirmBtn}
                  onClick={() => handleConfirmDonation(selectedDonation._id)}
                >
                  <CheckCircle size={18} />
                  Confirm Donation
                </button>
              )}
              {selectedDonation.status === 'completed' && (
                <button 
                  className={styles.refundBtn}
                  onClick={() => handleRefundDonation(selectedDonation._id)}
                >
                  <XCircle size={18} />
                  Refund
                </button>
              )}
              <button 
                className={styles.exportDetailBtn}
                onClick={() => toast.success(exportingDetails || 'Exporting donation details')}
              >
                <Download size={18} />
                {exportDetails || 'Export Details'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDonations;